namespace Portico.Models.Enterprise {
	import pmg = Portico.Models.Gateway;
	import psge = apigateway.enums;
	import psgm = apigateway.models;

    export interface IBaseAccount {
		id: number;
		displayId: string;
		type: string;
		status: string;
		partyId: number;
		securePartyId: string;
		programId: number;
		assignedUser: number;
		lastModifiedBy: number;
		lastModifiedTime: Date;
		accountAddresses: AccountAddress[];
		accountSessions: AccountSession[];
		attestations: Attestation[];
		contacts: Contact[];
		contactPreferences: ContactPreference[];
		externalIds: ExternalId[];
		linkedPayors: LinkedPayor[];
		notes: number[];
		optionalServices: OptionalService[];
		phoneNumbers: PhoneNumber[];
		tasks: number[];
		voiceMailOptIns: VoiceMailOptIn[];
		documentRelationships: DocumentRelationship[];
		payorRelationships: PayorRelationship[];
		pharmacyRelationships: PharmacyRelationship[];
		prescriberRelationships: PrescriberRelationship[];
		prescriptions: Prescription[];
		siteRelationships: SiteRelationship[];
		assignmentOfBenefitFlag: boolean;
		isIneligibleForMailInClaims: boolean;
		allowAutoReenroll: boolean;

		addAddress(index?: number): AccountAddress;
		addAttestation(index?: number): Attestation;
		addContact(index?: number): Contact;
		addContactPreference(index?: number): ContactPreference;
		addDocument(index?: number): DocumentRelationship;
		addExternalId(index?: number): ExternalId;
		addLinkedPayor(index?: number): LinkedPayor;
		addOptionalService(index?: number): OptionalService;
		addPayor(role: psge.PayorType, index?: number): PayorRelationship;
		addPharmacy(index?: number): PharmacyRelationship;
		addPhoneNumber(index?: number): PhoneNumber;
		addPrescriber(index?: number): PrescriberRelationship;
		addSite(index?: number): SiteRelationship;
		addVoiceMailOptIn(index?: number): VoiceMailOptIn;

		filterPayors(role: psge.PayorType): PayorRelationship[];

		getDiagnosisSession(create?: boolean): DiagnosisSession;
		getPayor(role: psge.PayorType, index: number): PayorRelationship;
		getSurveySession(surveyId: number, create?: boolean): SurveySession;
		getSurveySessionIds(): number[];

		removePayor(relationship: PayorRelationship): void;
		removePharmacy(relationship: PharmacyRelationship): void;
		removePrescriber(relationship: PrescriberRelationship): void;
		removeSite(relationship: SiteRelationship): void;

		mapToEnterpriseCoordinatorAccountUpsearchModel(): psgm.EnterpriseCoordinatorAccountServiceModelsAccountUpsearchModel;
		mapToEnterpriseCoordinatorAccountUpsearchModelWithFlags(canUpdate: boolean, canInsert: boolean, skipSearch: boolean): psgm.UpsearchModelEnterpriseCoordinatorAccountServiceModelsAccountUpsearchModel;
	}

	export class BaseAccount implements IBaseAccount {
		id: number;
		displayId: string;
		type: string;
		status: string;
		partyId: number;
		securePartyId: string;
		programId: number;
		assignedUser: number;
		lastModifiedBy: number;
		lastModifiedTime: Date;
		accountAddresses: AccountAddress[];
		accountSessions: AccountSession[];
		attestations: Attestation[];
		contacts: Contact[];
		contactPreferences: ContactPreference[];
		externalIds: ExternalId[];
		linkedPayors: LinkedPayor[];
		notes: number[];
		optionalServices: OptionalService[];
		phoneNumbers: PhoneNumber[];
		prescriptions: Prescription[];
		tasks: number[];
		voiceMailOptIns: VoiceMailOptIn[];
		documentRelationships: DocumentRelationship[];
		payorRelationships: PayorRelationship[];
		pharmacyRelationships: PharmacyRelationship[];
		prescriberRelationships: PrescriberRelationship[];
		siteRelationships: SiteRelationship[];
		assignmentOfBenefitFlag: boolean;
		isIneligibleForMailInClaims: boolean;
		allowAutoReenroll: boolean;

		constructor(obj?: IBaseAccount | any) {
            this.id = null;
			this.displayId = null;
            this.type = null;
            this.status = null;
			this.partyId = null;
			this.securePartyId = null;
			this.programId = null;
            this.assignedUser = null;
            this.lastModifiedBy = null;
			this.lastModifiedTime = null;
			this.accountAddresses = [];
			this.accountSessions = [];
			this.attestations = [];
			this.contacts = [];
			this.contactPreferences = [];
			this.externalIds = [];
			this.linkedPayors = [];
			this.notes = [];
			this.optionalServices = [];
			this.phoneNumbers = [];
			this.prescriptions = [];
            this.tasks = [];
            this.voiceMailOptIns = [];
			this.documentRelationships = [];
			this.payorRelationships = [];
			this.pharmacyRelationships = [];
			this.prescriberRelationships = [];
			this.siteRelationships = [];
			this.assignmentOfBenefitFlag = false;
			this.isIneligibleForMailInClaims = false;
			this.allowAutoReenroll = false

			if (obj) {
                this.id = obj.id || obj.accountId;
				this.displayId = obj.displayId;
                this.type = obj.type || obj.accountType;
                this.status = obj.status || obj.accountStatus;
				this.partyId = obj.partyId;
				this.securePartyId = obj.securePartyId;
				this.programId = obj.programId;
				this.assignedUser = obj.assignedUser;
				this.lastModifiedBy = obj.lastModifiedBy;
				this.assignmentOfBenefitFlag = obj.assignmentOfBenefitFlag || false;
				this.isIneligibleForMailInClaims = obj.isIneligibleForMailInClaims || false;
				this.allowAutoReenroll = obj.allowAutoReenroll || false;

				if (obj.lastModifiedTime) {
					this.lastModifiedTime = new Date(obj.lastModifiedTime.toString());
				}

				if (obj.accountAddresses) {
					for (const accountAddress of obj.accountAddresses) {
						this.accountAddresses.push(new AccountAddress(accountAddress));
					}
				}

				if (obj.accountSessions) {
					for (const accountSession of obj.accountSessions) {
						this.accountSessions.push(new AccountSession(accountSession));
					}
				}

				if (obj.attestations) {
					for (const attestation of obj.attestations) {
						this.attestations.push(new Attestation(attestation));
					}
				}

				if (obj.contacts || obj.accountContacts) {
					for (const contact of obj.contacts || obj.accountContacts) {
						this.contacts.push(new Contact(contact));
					}
				}

				if (obj.contactPreferences) {
					for (const contactPreference of obj.contactPreferences) {
						this.contactPreferences.push(new ContactPreference(contactPreference));
					}
				}

				if (obj.externalIds) {
					for (const externalId of obj.externalIds) {
						this.externalIds.push(new ExternalId(externalId));
					}
				}

				if (obj.linkedPayors) {
					for (const linkedPayor of obj.linkedPayors) {
						this.linkedPayors.push(new LinkedPayor(linkedPayor));
					}
				}

				if (obj.notes) {
					this.notes = obj.notes;
				}

				if (obj.optionalServices) {
					for (const optionalService of obj.optionalServices) {
						this.optionalServices.push(new OptionalService(optionalService));
					}
				}

				if (obj.phoneNumbers || obj.accountPhoneNumbers) {
					for (const phoneNumber of obj.phoneNumbers || obj.accountPhoneNumbers) {
						this.phoneNumbers.push(new PhoneNumber(phoneNumber));
					}
				}

				if (obj.prescriptions) {
					for (const prescription of obj.prescriptions) {
						this.prescriptions.push(new Prescription(prescription));
					}
				}

				if (obj.tasks) {
					this.tasks = obj.tasks;
				}

                if (obj.voiceMailOptIns) {
                    for (const voiceMailOptIn of obj.voiceMailOptIns) {
                        this.voiceMailOptIns.push(new VoiceMailOptIn(voiceMailOptIn));
                    }
                }

				if (obj.documentRelationships || obj.documents) {
					for (const documentRelationship of obj.documentRelationships || obj.documents) {
						this.documentRelationships.push(new DocumentRelationship(documentRelationship));
					}
				}

				if (obj.payorRelationships) {
					for (const payorRelationship of obj.payorRelationships) {
						this.payorRelationships.push(new PayorRelationship(payorRelationship));
					}
				} else if (obj.payors) {
					for (const role of obj.payors) {
						for (const relation of role.accountRelations) {
							this.payorRelationships.push(new PayorRelationship(<IPayorRelationship>{ payorId: relation.relatedId, role: role.roleType, order: relation.coverageOrder, isPrimary: relation.isPrimary }));
						}
					}
                }

				if (obj.pharmacyRelationships || obj.pharmacies) {
					for (const pharmacyRelationship of obj.pharmacyRelationships || obj.pharmacies) {
						this.pharmacyRelationships.push(new PharmacyRelationship(pharmacyRelationship));
                    }
                }

				if (obj.prescriberRelationships || obj.prescribers) {
					for (const prescriberRelationship of obj.prescriberRelationships || obj.prescribers) {
						this.prescriberRelationships.push(new PrescriberRelationship(prescriberRelationship));
                    }
                }

				if (obj.siteRelationships || obj.sites) {
					for (const siteRelationship of obj.siteRelationships || obj.sites) {
						this.siteRelationships.push(new SiteRelationship(siteRelationship));
                    }
                }
			}
        }

		addAddress(index: number = null): AccountAddress {
			const length = this.accountAddresses.length;

			if (index && index < length) {
				return this.accountAddresses[index];
			}

			const accountAddress = new AccountAddress();
			this.accountAddresses.push(accountAddress);
			return accountAddress;
		}

		addAttestation(index: number = null): Attestation {
			const length = this.attestations.length;

			if (index && index < length) {
				return this.attestations[index];
			}

			const attestation = new Attestation();
			this.attestations.push(attestation);
			return attestation;
		}

		addContact(index: number = null): Contact {
			const length = this.contacts.length;

			if (index && index < length) {
				return this.contacts[index];
			}

			const contact = new Contact();
			this.contacts.push(contact);
			return contact;
		}

		addContactPreference(index: number = null): ContactPreference {
			const length = this.contactPreferences.length;

			if (index && index < length) {
				return this.contactPreferences[index];
			}

			const contactPreference = new ContactPreference();
			this.contactPreferences.push(contactPreference);
			return contactPreference;
		}

		addDocument(index: number = null): DocumentRelationship {
			const length = this.documentRelationships.length;

			if (index && index < length) {
				return this.documentRelationships[index];
			}

			const relationship = new DocumentRelationship();
			this.documentRelationships.push(relationship);
			return relationship;
		}

		addExternalId(index: number = null): ExternalId {
			const length = this.externalIds.length;

			if (index && index < length) {
				return this.externalIds[index];
			}

			const externalId = new ExternalId();
			this.externalIds.push(externalId);
			return externalId;
		}

		addLinkedPayor(index: number = null): LinkedPayor {
			const length = this.linkedPayors.length;

			if (index && index < length) {
				return this.linkedPayors[index];
			}

			const linkedPayor = new LinkedPayor();
			this.linkedPayors.push(linkedPayor);
			return linkedPayor;
		}

		addOptionalService(index: number = null): OptionalService {
			const length = this.optionalServices.length;

			if (index && index < length) {
				return this.optionalServices[index];
			}

			const optionalService = new OptionalService();
			this.optionalServices.push(optionalService);
			return optionalService;
		}

		addPayor(role: psge.PayorType, index: number = null): PayorRelationship {
			const matches = this.filterPayors(role);

			if (index && index < matches.length) {
				return matches[index];
			}

			const relationship = new PayorRelationship(<IPayorRelationship>{
				role: role,
				order: matches.length + 1,
				isPrimary: matches.length === 0,
				payor: new Payor(<IPayor>{ type: role }),
				payorBenefit: new PayorBenefit()
			});

			this.payorRelationships.push(relationship);

			return relationship;
		}

		addPharmacy(index: number = null): PharmacyRelationship {
			const length = this.pharmacyRelationships.length;

			if (index && index < length) {
				return this.pharmacyRelationships[index];
			}

			const relationship = new PharmacyRelationship(<IPharmacyRelationship>{
				isPrimary: length === 0,
				pharmacy: new Pharmacy()
			});

			this.pharmacyRelationships.push(relationship);

			return relationship;
		}

		addPhoneNumber(index: number = null): PhoneNumber {
			const length = this.phoneNumbers.length;

			if (index && index < length) {
				return this.phoneNumbers[index];
			}

			const phoneNumber = new PhoneNumber();
			this.phoneNumbers.push(phoneNumber);
			return phoneNumber;
		}

		addPrescriber(index: number = null): PrescriberRelationship {
			const length = this.prescriberRelationships.length;

			if (index && index < length) {
				return this.prescriberRelationships[index];
			}

			const relationship = new PrescriberRelationship(<IPrescriberRelationship>{
				isPrimary: length === 0,
				prescriber: new Prescriber()
			});

			this.prescriberRelationships.push(relationship);

			return relationship;
		}

		addSite(index: number = null): SiteRelationship {
			const length = this.siteRelationships.length;

			if (index && index < length) {
				return this.siteRelationships[index];
			}

			const relationship = new SiteRelationship(<ISiteRelationship>{
				isPrimary: length === 0,
				site: new Site()
			});

			this.siteRelationships.push(relationship);

			return relationship;
		}

		addVoiceMailOptIn(index: number = null): VoiceMailOptIn {
			const length = this.voiceMailOptIns.length;

			if (index && index < length) {
				return this.voiceMailOptIns[index];
			}

			const voiceMailOptIn = new VoiceMailOptIn();
			this.voiceMailOptIns.push(voiceMailOptIn);
			return voiceMailOptIn;
		}

		filterPayors(role: psge.PayorType): PayorRelationship[] {
			let matches = this.payorRelationships.filter((payorRelationship) => {
				return payorRelationship.role === role;
			});

			return matches;
		}

		getDiagnosisSession(create: boolean = true): DiagnosisSession {
			const accountSessions = this.accountSessions.filter((accountSession) => {
				return !accountSession.id && accountSession.diagnosisSession;
			});

			if (accountSessions && accountSessions.length > 0) {
				return accountSessions[0].diagnosisSession;
			}

			if (!create) {
				return null;
			}

			const diagnosisSession = new DiagnosisSession();

			const accountSession = new AccountSession(<IAccountSession>{
				diagnosisSession: diagnosisSession
			});

			this.accountSessions.push(accountSession);

			return accountSession.diagnosisSession;
		}

		getPayor(role: psge.PayorType, index: number): PayorRelationship {
			const matches = this.filterPayors(role);
			return index < matches.length ? matches[index] : null;
		}

		getSurveySession(surveyId: number, create: boolean = true): SurveySession {
			const accountSessions = this.accountSessions.filter((accountSession) => {
				return !accountSession.id && accountSession.surveySession && accountSession.surveySession.surveyId === surveyId;
			});

			if (accountSessions && accountSessions.length > 0) {
				return accountSessions[0].surveySession;
			}

			if (!create) {
				return null;
			}

			const surveySession = new SurveySession(<ISurveySession>{
				surveyId: surveyId
			});

			const accountSession = new AccountSession(<IAccountSession>{
				surveyId: surveyId,
				surveySession: surveySession
			});

			this.accountSessions.push(accountSession);

			return accountSession.surveySession;
		}

		getSurveySessionIds(): number[] {
			var surveySessionIds: number[] = [];

			if (this.accountSessions && this.accountSessions.length > 0) {
				var surveysWithSurveySession = _.filter(this.accountSessions, x => { return x.surveySession && x.surveySession.surveySessionId });

				surveySessionIds = _.map(surveysWithSurveySession as AccountSession[], srvy => {
					return srvy.surveySession.surveySessionId
				})
			}

			return surveySessionIds;
		}

		removePayor(relationship: PayorRelationship): void {
			const matches = this.payorRelationships.filter((payorRelationship) => {
				return payorRelationship !== relationship;
			});

			this.payorRelationships = matches;
		}

		removePharmacy(relationship: PharmacyRelationship): void {
			const matches = this.pharmacyRelationships.filter((pharmacyRelationship) => {
				return pharmacyRelationship !== relationship;
			});

			this.pharmacyRelationships = matches;
		}

		removePrescriber(relationship: PrescriberRelationship): void {
			const matches = this.prescriberRelationships.filter((prescriberRelationship) => {
				return prescriberRelationship !== relationship;
			});

			this.prescriberRelationships = matches;
		}

		removeSite(relationship: SiteRelationship): void {
			const matches = this.siteRelationships.filter((siteRelationship) => {
				return siteRelationship !== relationship;
			});

			this.siteRelationships = matches;
		}

        mapToEnterpriseCoordinatorAccountUpsearchModel(): psgm.EnterpriseCoordinatorAccountServiceModelsAccountUpsearchModel {
            return {
                accountId: this.id,
                accountStatus: Portico.Models.Gateway.AccountStatus[this.status],
                accountType: Portico.Models.Gateway.AccountType[this.type],
				addresses: this.accountAddresses ? _.map(this.accountAddresses, addr => {
                    return {
                        id: addr.id,
                        receiverFirstName: addr.receiverFirstName,
                        receiverLastName: addr.receiverLastName,
                        addressType: Portico.Models.Gateway.AddressType[addr.type],
                        addressOne: addr.address1,
                        addressTwo: addr.address2,
                        city: addr.city,
                        state: addr.state,
                        zip: addr.zip,
                        country: addr.country
                    } as psgm.EnterpriseCoordinatorAccountServiceModelsAccountAddressUpsearchModel
                }) : [],
                assignedUser: this.assignedUser,
                contacts: this.contacts ? _.map(this.contacts, cnt => {
                    return {
                        id: cnt.id,
                        roleType: Portico.Models.Gateway.RoleType[cnt.type],
                        firstName: cnt.firstName,
                        lastName: cnt.lastName,
                        phoneNumber: cnt.phoneNumbers && cnt.phoneNumbers.length > 0 ? cnt.phoneNumbers[0].number : [],
                        altPhoneNumber: cnt.phoneNumbers && cnt.phoneNumbers.length > 1 ? cnt.phoneNumbers[1].number : [],
                    } as psgm.EnterpriseCoordinatorAccountServiceModelsAccountContactUpsearchModel
                }) : [],
                contactPreferences: this.contactPreferences ? _.map(this.contactPreferences, cntpref => {
                    return {
                        contactInfoType: cntpref.contactInfoType,
                        isOptedIn: cntpref.isOptedIn,
                        isPreferred: cntpref.isPreferred
                    } as psgm.EnterpriseCoordinatorAccountServiceModelsAccountContactPreferenceModel
                }) : [],
                documents: _.map(this.documentRelationships, d => {
                    return {
                        documentId: d.documentId,
                        comment: d.comment
                    } as psgm.EnterpriseCoordinatorCaseDocumentRelationshipModel
                }),
				expireExistingAccountRelationships: true,
				externalIds: this.externalIds ? _.map(this.externalIds, e => {
					return {
						externalIdType: typeof e.type === 'number' ? e.type : Portico.Models.Gateway.AccountExternalIdType[e.type],
						externalId: e.value
					} as psgm.EnterpriseCoordinatorAccountServiceModelsAccountExternalIdModel
				}) : [],
				phoneNumbers: this.phoneNumbers ? _.map(this.phoneNumbers, ph => {
                    return {
                        id: ph.id,
                        phoneType: ph.type,
                        phoneNumber: ph.number,
                        extension: ph.extension,
                        isVoicemailOptedIn: ph.isVoicemailOptedIn,
                        isPrimary: ph.isPrimary
                    } as psgm.EnterpriseCoordinatorAccountServiceModelsAccountPhoneUpsearchModel
                }) : [],
                notes: this.notes,
				tasks: this.tasks,
				voicemailOptInStatus: this.voiceMailOptIns ? _.map(this.voiceMailOptIns, vm => {
                    return {
                        phoneNumberId: vm.phoneNumberId,
                        isVmOptedIn: vm.isVmOptedIn

                    } as psgm.EnterpriseCoordinatorAccountServiceModelsVoicemailOptInModel
				}) : [],
				assignmentOfBenefitFlag: this.assignmentOfBenefitFlag,
				allowAutoReenroll: this.allowAutoReenroll,
            } as psgm.EnterpriseCoordinatorAccountServiceModelsAccountUpsearchModel
		}

		mapToEnterpriseCoordinatorAccountUpsearchModelWithFlags(canUpdate: boolean, canInsert: boolean, skipSearch: boolean): psgm.UpsearchModelEnterpriseCoordinatorAccountServiceModelsAccountUpsearchModel {
			return {
				canUpdate: canUpdate,
				canInsert: canInsert,
				skipSearch: skipSearch,
				model: this.mapToEnterpriseCoordinatorAccountUpsearchModel()
			};
		}
	}

	export interface IDocumentRelationship {
		documentId: number;
		comment: string;
	}

	export class DocumentRelationship implements IDocumentRelationship {
		documentId: number;
		comment: string;

		constructor(obj?: IDocumentRelationship) {
			this.documentId = null;
			this.comment = null;

			if (obj) {
				this.documentId = obj.documentId;
				this.comment = obj.comment;
			}
		}
	}

	export interface IPayorRelationship {
		payorId: number;
		role: psge.PayorType;
		order: number;
		isPrimary: boolean;
		payor: Payor;
		payorBenefit: PayorBenefit;
		coverage: string;

		mapToEnterpriseCoordinatorPayorUpsearchModel(): psgm.EnterpriseCoordinatorPayorServiceModelsPayorUpsearchModel;
		mapToEnterpriseCoordinatorPayorUpsearchModelWithFlags(canUpdate: boolean, canInsert: boolean, skipSearch: boolean): psgm.UpsearchModelEnterpriseCoordinatorPayorServiceModelsPayorUpsearchModel;
	}

	export class PayorRelationship implements IPayorRelationship {
		payorId: number;
		role: psge.PayorType;
		order: number;
		isPrimary: boolean;
		payor: Payor;
		payorBenefit: PayorBenefit;
		coverage: string;

		constructor(obj?: Partial<IPayorRelationship>) {
			this.payorId = null;
			this.role = null;
			this.order = null;
			this.isPrimary = null;
			this.payor = null;
			this.payorBenefit = null;
			this.coverage = null;

			if (obj) {
				this.payorId = obj.payorId;
				this.role = obj.role;
				this.order = obj.order;
				this.isPrimary = obj.isPrimary;

				if (obj.payor) {
					this.payor = new Payor(obj.payor);
				}

				if (obj.payorBenefit) {
					this.payorBenefit = new PayorBenefit(obj.payorBenefit);
				}

				this.coverage = obj.coverage;
			}
		}

		mapToEnterpriseCoordinatorPayorUpsearchModel() : psgm.EnterpriseCoordinatorPayorServiceModelsPayorUpsearchModel {
            if (!this.payor) {
				return {
					payorId: this.payorId
				} as psgm.EnterpriseCoordinatorPayorServiceModelsPayorUpsearchModel;
			}

			return {
				payorId: this.payor.id ? this.payor.id : this.payorId,
				payorName: this.payor.name,
				payorType: this.payor.type,
				payorBin: this.payor.bin,
				payorPcn: this.payor.pcn,
				payorGroup: this.payor.group,
				isPlaceholder: this.payor.isPlaceholder,
				accountRoleType: <psge.RoleType><unknown>this.payor.type,
				accountRelationshipCoverageOrder: this.order,
				accountRelationshipIsPrimary: this.isPrimary,
				addresses: this.payor.addresses ? _.map(this.payor.addresses, addr => {
					return addr.mapToEnterpriseCoordinatorAddress();
				}) : [],
				emailAddresses: this.payor.emailAddresses ? _.map(this.payor.emailAddresses, email => {
					return email.mapToEnterpriseCoordinatorEmailAddress();
				}) : [],
				phoneNumbers: this.payor.phoneNumbers ? _.map(this.payor.phoneNumbers, ph => {
					return ph.mapToEnterpriseCoordinatorPhoneUpsearchModel();
				}) : [],
				webAddresses: this.payor.webAddresses ? _.map(this.payor.webAddresses, webAddr => {
					return webAddr.mapToEnterpriseCoordinatorWebAddress();
				}) : [],
				contacts:[],
				payorBenefit: {
					dateOfBirth: _.get(this, 'payorBenefit.dateOfBirth', null),
					policyHolderFirstName: _.get(this, 'payorBenefit.policyHolderFirstName', null),
					policyHolderLastName: _.get(this, 'payorBenefit.policyHolderLastName', null),
					relationshipToPatient: _.get(this, 'payorBenefit.relationshipToPatient', null),
					phone: _.get(this, 'payorBenefit.phone', null),
					groupNumber: _.get(this, 'payorBenefit.groupNumber', null),
					policyNumber: _.get(this, 'payorBenefit.policyNumber', null),
					personCode: _.get(this, 'payorBenefit.personCode', null),
					planType: _.get(this, 'payorBenefit.planType', null),
					planEffectiveDate: _.get(this, 'payorBenefit.planEffectiveDate', null),
					planExpirationDate: _.get(this, 'payorBenefit.planExpirationDate', null),
					planTerminationDate: _.get(this, 'payorBenefit.planTerminationDate', null),
					policyName: _.get(this, 'payorBenefit.policyName', null),
					bin: _.get(this, 'payorBenefit.bin', null),
					pcn: _.get(this, 'payorBenefit.pcn', null)
				} as psgm.EnterpriseCoordinatorPayorServiceModelsPayorBenefitModel
			} as psgm.EnterpriseCoordinatorPayorServiceModelsPayorUpsearchModel;
		}

		mapToEnterpriseCoordinatorPayorUpsearchModelWithFlags(canUpdate: boolean, canInsert: boolean, skipSearch: boolean): psgm.UpsearchModelEnterpriseCoordinatorPayorServiceModelsPayorUpsearchModel {
			return {
				canUpdate: canUpdate,
				canInsert: canInsert,
				skipSearch: skipSearch,
				model: this.mapToEnterpriseCoordinatorPayorUpsearchModel()
			};
		}
	}

	export interface IPharmacyRelationship {
		pharmacyId: number;
		isPrimary: boolean;
		pharmacy: Pharmacy;

		pointOfContacts: PointOfContact[];

		addPointOfContact(index: number): PointOfContact;
		mapToEnterpriseCoordinatorPharmacyUpsearchModel(): psgm.EnterpriseCoordinatorPharmacyServiceModelsPharmacyUpsearchModel;
		mapToEnterpriseCoordinatorPharmacyUpsearchModelWithFlags(canUpdate: boolean, canInsert: boolean, skipSearch: boolean): psgm.UpsearchModelEnterpriseCoordinatorPharmacyServiceModelsPharmacyUpsearchModel;
	}

	export class PharmacyRelationship implements IPharmacyRelationship {
		pharmacyId: number;
		isPrimary: boolean;
		pharmacy: Pharmacy;

        pointOfContacts: PointOfContact[];

		constructor(obj?: IPharmacyRelationship | any) {
			this.pharmacyId = null;
			this.isPrimary = null;
            this.pharmacy = null;
            this.pointOfContacts = []

			if (obj) {
				this.pharmacyId = obj.pharmacyId || obj.relatedId;
                this.isPrimary = obj.isPrimary;

				if (obj.pharmacy) {
					this.pharmacy = new Pharmacy(obj.pharmacy);
                }
                if (obj.pointOfContacts) {
                    for (const pointOfContact of obj.pointOfContacts) {
                        this.pointOfContacts.push(new PointOfContact(pointOfContact));
                    }
                }
			}
		}

		addPointOfContact(index: number = null): PointOfContact {
			const length = this.pointOfContacts.length;

			if (index && index < length) {
				return this.pointOfContacts[index];
			}

			const pointOfContact = new PointOfContact();
			this.pointOfContacts.push(pointOfContact);
			return pointOfContact;
		}

		mapToEnterpriseCoordinatorPharmacyUpsearchModel() : psgm.EnterpriseCoordinatorPharmacyServiceModelsPharmacyUpsearchModel {
			if (!this.pharmacy) {
				return {
					pharmacyId: this.pharmacyId
				} as psgm.EnterpriseCoordinatorPharmacyServiceModelsPharmacyUpsearchModel;
			}

			return {
				pharmacyId: this.pharmacyId ? this.pharmacyId : this.pharmacy.id,
				pharmacyName: this.pharmacy.name,
				nabp: this.pharmacy.nabp,
				npi: this.pharmacy.npi,
				pharmacyType: Portico.Models.Gateway.PharmacyType[this.pharmacy.type],
				pharmacySpecialization: Portico.Models.Gateway.PharmacySpecialization[this.pharmacy.specialization],
				accountRelationshipIsPrimary: this.isPrimary,
				addresses: this.pharmacy.addresses ? _.map(this.pharmacy.addresses, addr => {
					return addr.mapToEnterpriseCoordinatorAddress();
				}) : [],
				phoneNumbers: this.pharmacy.phoneNumbers ? _.map(this.pharmacy.phoneNumbers, ph => {
					return ph.mapToEnterpriseCoordinatorPhoneUpsearchModel();
				}) : [],
				emailAddresses: this.pharmacy.emailAddresses ? _.map(this.pharmacy.emailAddresses, email => {
					return email.mapToEnterpriseCoordinatorEmailAddress();
				}) : [],
				webAddresses: this.pharmacy.webAddresses ? _.map(this.pharmacy.webAddresses, webAddr => {
					return webAddr.mapToEnterpriseCoordinatorWebAddress();
				}) : [],
				contacts: this.pharmacy.contacts ? _.map(this.pharmacy.contacts, cntct => {
					return cntct.mapToEnterpriseCoordinatorContact();
				}) : [],
				pointOfContacts: this.pointOfContacts ? _.map(this.pointOfContacts, cntc => {
					return {
						id: cntc.id,
						firstName: cntc.firstName,
						lastName: cntc.lastName,
						phoneNumber: cntc.phoneNumber,
						phoneNumberExtension: cntc.phoneNumberExtension,
						faxNumber: cntc.faxNumber
					} as psgm.EnterpriseCoordinatorPharmacyServiceModelsPharmacyPointOfContactUpsearchModel
				}) : []
			} as psgm.EnterpriseCoordinatorPharmacyServiceModelsPharmacyUpsearchModel;
        }

		mapToEnterpriseCoordinatorPharmacyUpsearchModelWithFlags(canUpdate: boolean, canInsert: boolean, skipSearch: boolean): psgm.UpsearchModelEnterpriseCoordinatorPharmacyServiceModelsPharmacyUpsearchModel {
			return {
				canUpdate: canUpdate,
				canInsert: canInsert,
				skipSearch: skipSearch,
				model: this.mapToEnterpriseCoordinatorPharmacyUpsearchModel()
			};
		}
	}

	export interface IPrescriberRelationship {
		prescriberId: number;
		isPrimary: boolean;
		prescriber: Prescriber;

		mapToEnterpriseCoordinatorPrescriberUpsearchModel(): psgm.EnterpriseCoordinatorPrescriberServiceModelsPrescriberUpsearchModel;
		mapToEnterpriseCoordinatorPrescriberUpsearchModelWithFlags(canUpdate: boolean, canInsert: boolean, skipSearch: boolean): psgm.UpsearchModelEnterpriseCoordinatorPrescriberServiceModelsPrescriberUpsearchModel;
	}

	export class PrescriberRelationship implements IPrescriberRelationship {
		prescriberId: number;
		isPrimary: boolean;
		prescriber: Prescriber;

		constructor(obj?: IPrescriberRelationship | any) {
			this.prescriberId = null;
			this.isPrimary = null;
			this.prescriber = null;

			if (obj) {
				this.prescriberId = obj.prescriberId || obj.relatedId;
				this.isPrimary = obj.isPrimary;

				if (obj.prescriber) {
					this.prescriber = new Prescriber(obj.prescriber);
				}
			}
		}

		mapToEnterpriseCoordinatorPrescriberUpsearchModel() : psgm.EnterpriseCoordinatorPrescriberServiceModelsPrescriberUpsearchModel {
            if(!this.prescriber) {
				return {
					prescriberId: this.prescriberId
				} as psgm.EnterpriseCoordinatorPrescriberServiceModelsPrescriberUpsearchModel;
			}

			return {
				prescriberId: this.prescriber.id ? this.prescriber.id : this.prescriberId,
				firstName: this.prescriber.firstName,
				middleName: this.prescriber.middleName,
				lastName: this.prescriber.lastName,
				suffix: this.prescriber.suffix,
				nickname: this.prescriber.nickName,
				npi: this.prescriber.npi,
				taxId: this.prescriber.taxId,
				accountRelationshipIsPrimary: this.isPrimary,
				prescriberLicenses: this.prescriber.prescriberLicenses ? _.map(this.prescriber.prescriberLicenses, pl => {
					return {
						prescriberLicenseId: pl.prescriberLicenseId,
						licenseType: pl.licenseType,
						licenseNumber: pl.licenseNumber
					} as psgm.EnterpriseCoordinatorPrescriberServiceModelsPrescriberLicenseUpsearchModel
				}) : [],
				externalIds: this.prescriber.externalIds ? _.map(this.prescriber.externalIds, extId => {
					return extId.mapToEnterpriseCoordinatorExternalId();
				}) : [],
				addresses: this.prescriber.addresses ? _.map(this.prescriber.addresses, addr => {
					return addr.mapToEnterpriseCoordinatorAddress();
				}) : [],
				phoneNumbers: this.prescriber.phoneNumbers ? _.map(this.prescriber.phoneNumbers, ph => {
					return ph.mapToEnterpriseCoordinatorPhoneUpsearchModel();
				}) : [],
				emailAddresses: this.prescriber.emailAddresses ? _.map(this.prescriber.emailAddresses, email => {
					return email.mapToEnterpriseCoordinatorEmailAddress();
				}) : []
			} as psgm.EnterpriseCoordinatorPrescriberServiceModelsPrescriberUpsearchModel;
        }

		mapToEnterpriseCoordinatorPrescriberUpsearchModelWithFlags(canUpdate: boolean, canInsert: boolean, skipSearch: boolean): psgm.UpsearchModelEnterpriseCoordinatorPrescriberServiceModelsPrescriberUpsearchModel {
			return {
				canUpdate: canUpdate,
				canInsert: canInsert,
				skipSearch: skipSearch,
				model: this.mapToEnterpriseCoordinatorPrescriberUpsearchModel()
			};
		}
	}

	export interface ISiteRelationship {
		siteId: number;
		isPrimary: boolean;
		site: Site;

		pointOfContacts: PointOfContact[];

		addPointOfContact(index: number): PointOfContact;
		mapToEnterpriseCoordinatorSiteUpsearchModel(): psgm.EnterpriseCoordinatorSiteServiceModelsSiteUpsearchModel;
		mapToEnterpriseCoordinatorSiteUpsearchModelWithFlags(canUpdate: boolean, canInsert: boolean, skipSearch: boolean): psgm.UpsearchModelEnterpriseCoordinatorSiteServiceModelsSiteUpsearchModel;
	}

	export class SiteRelationship implements ISiteRelationship {
		siteId: number;
		isPrimary: boolean;
		site: Site;

        pointOfContacts: PointOfContact[];

		constructor(obj?: ISiteRelationship | any) {
			this.siteId = null;
			this.isPrimary = null;
            this.site = null;
            this.pointOfContacts = []

			if (obj) {
				this.siteId = obj.siteId || obj.relatedId;
				this.isPrimary = obj.isPrimary;

				if (obj.site) {
					this.site = new Site(obj.site);
                }

                if (obj.pointOfContacts) {
                    for (const pointOfContact of obj.pointOfContacts) {
                        this.pointOfContacts.push(new PointOfContact(pointOfContact));
                    }
                }
			}
		}

		addPointOfContact(index: number = null): PointOfContact {
			const length = this.pointOfContacts.length;

			if (index && index < length) {
				return this.pointOfContacts[index];
			}

			const pointOfContact = new PointOfContact();
			this.pointOfContacts.push(pointOfContact);
			return pointOfContact;
		}

		mapToEnterpriseCoordinatorSiteUpsearchModel() : psgm.EnterpriseCoordinatorSiteServiceModelsSiteUpsearchModel {
			if (!this.site) {
				return {
					siteId: this.siteId
				} as psgm.EnterpriseCoordinatorSiteServiceModelsSiteUpsearchModel;
			}

			return {
				siteId: this.site.id ? this.site.id : this.siteId,
				siteRoleType: this.site.roleType,
				description: this.site.description,
				nickName: this.site.nickName,
				businessStartTime: moment(this.site.businessStartTime),
				businessEndTime: moment(this.site.businessEndTime),
				accountRelationshipIsPrimary: this.isPrimary,
				addresses: this.site.addresses ? _.map(this.site.addresses, addr => {
					return addr.mapToEnterpriseCoordinatorAddress();
				}) : [],
				contacts: this.site.contacts ? _.map(this.site.contacts, cntct => {
					return cntct.mapToEnterpriseCoordinatorContact();
				}) : [],
				emailAddresses: this.site.emailAddresses ? _.map(this.site.emailAddresses, email => {
					return email.mapToEnterpriseCoordinatorEmailAddress();
				}) : [],
				externalIds: this.site.externalIds? _.map(this.site.externalIds, extId => {
					return extId.mapToEnterpriseCoordinatorExternalId();
				}) : [],
				globalIds: this.site.globalIds? _.map(this.site.globalIds, glbId => {
					return glbId.mapToEnterpriseCoordinatorGlobalId();
				}) : [],
				phoneNumbers: this.site.phoneNumbers ? _.map(this.site.phoneNumbers, ph => {
					return ph.mapToEnterpriseCoordinatorPhoneUpsearchModel();
				}) : [],
				pointOfContacts: this.pointOfContacts ? _.map(this.pointOfContacts, cntc => {
					return {
						id: cntc.id,
						firstName: cntc.firstName,
						lastName: cntc.lastName,
						phoneNumber: cntc.phoneNumber,
						phoneNumberExtension: cntc.phoneNumberExtension,
						faxNumber: cntc.faxNumber,
						jobTitle: cntc.jobTitle,
						emailAddress: cntc.emailAddress,
						preferredContactMethod: Portico.Models.Gateway.ContactInfoType[cntc.preferredContactMethod]
					} as psgm.EnterpriseCoordinatorSiteServiceModelsSitePointOfContactUpsearchModel
				}) : []
			} as psgm.EnterpriseCoordinatorSiteServiceModelsSiteUpsearchModel;
        }

		mapToEnterpriseCoordinatorSiteUpsearchModelWithFlags(canUpdate: boolean, canInsert: boolean, skipSearch: boolean): psgm.UpsearchModelEnterpriseCoordinatorSiteServiceModelsSiteUpsearchModel {
			return {
				canUpdate: canUpdate,
				canInsert: canInsert,
				skipSearch: skipSearch,
				model: this.mapToEnterpriseCoordinatorSiteUpsearchModel()
			};
		}
	}
}